import { infiniteQueryOptions } from '@tanstack/react-query'

import { SearchUnifiedShipmentsResponse } from '@forto-dev/search-light-models'

import { extractAddressIDsFromWaypoints, getLegalEntities } from '@/features/bookings'
import { pickupDeliveryLocationClient } from '@/features/locations'
import { error as logError } from '@/utils/logger'

import { unifiedBookingToOverviewItem } from './unifiedBookingToOverviewItem'
import { getUnifiedShipments, PaginatedUnifiedShipments } from './unifiedShipments.service'
import { unifiedShipmentToOverviewItem } from './unifiedShipmentToOverviewItem'
import { withSearchFlags } from './withSearchFlags'
import { ResolvedAddressMap, ResolvedLegalEntityMap, ShipmentPageParams } from '../types'

const pageSize = 20

/**
 * Extracts addressIDs from booking waypoints (origin door and destination door)
 */
const extractAddressIDsFromBookings = (items: SearchUnifiedShipmentsResponse[]): string[] => {
  const addressIds = items.reduce<string[]>((acc, item) => {
    if (item.booking && item.booking.waypoints) {
      const addressIDs = extractAddressIDsFromWaypoints(item.booking.waypoints)
      acc.push(...addressIDs)
    }

    return acc
  }, [])

  // Return unique address IDs
  return Array.from(new Set(addressIds))
}

/**
 * Extracts legal entity IDs from bookings where shipper/consignee contact name is missing
 */
const extractLegalEntityIDsFromBookings = (items: SearchUnifiedShipmentsResponse[]): string[] => {
  const ids = new Set<string>()
  items.forEach((item) => {
    if (!item.booking) return
    if (!item.booking.consignee?.companyName && item.booking.consigneeLegalEntityID) {
      ids.add(item.booking.consigneeLegalEntityID)
    }
    if (!item.booking.shipper?.companyName && item.booking.shipperLegalEntityID) {
      ids.add(item.booking.shipperLegalEntityID)
    }
  })
  return Array.from(ids)
}

/**
 * Fetches legal entities by codes and returns a map of legalEntityCode to response
 */
const fetchResolvedLegalEntities = async (legalEntityIDs: string[]): Promise<ResolvedLegalEntityMap> => {
  if (legalEntityIDs.length === 0) {
    return new Map()
  }

  try {
    const response = await getLegalEntities({ code: legalEntityIDs })
    const map: ResolvedLegalEntityMap = new Map()
    response.items.forEach((le) => map.set(le.code, le))
    return map
  } catch (error) {
    logError('Failed to fetch resolved legal entities:', error)
    return new Map()
  }
}

/**
 * Fetches pickup/delivery locations by IDs and returns a map of addressID to location
 */
const fetchResolvedAddresses = async (addressIDs: string[]): Promise<ResolvedAddressMap> => {
  if (addressIDs.length === 0) {
    return new Map()
  }

  try {
    const response = await pickupDeliveryLocationClient.listPickupDeliveryLocations({
      locationIDs: addressIDs,
    })

    const addressMap: ResolvedAddressMap = new Map()
    response.items.forEach((location) => {
      addressMap.set(location.id, location)
    })

    return addressMap
  } catch (error) {
    // Log error but don't fail the whole request, addresses will just be empty
    logError('Failed to fetch resolved addresses:', error)
    return new Map()
  }
}

const getOverviewList = async ({
  pageParam,
}: {
  pageParam: Partial<ShipmentPageParams>
}): Promise<PaginatedUnifiedShipments> => {
  const { items, totalItems, totalPages } = await getUnifiedShipments({ pageParam })

  // Extract IDs from bookings and fetch resolved data in parallel
  const addressIDs = extractAddressIDsFromBookings(items)
  const legalEntityIDs = extractLegalEntityIDsFromBookings(items)
  const [resolvedAddresses, resolvedLegalEntities] = await Promise.all([
    fetchResolvedAddresses(addressIDs),
    fetchResolvedLegalEntities(legalEntityIDs),
  ])

  const overviewItems = items.map((item) => {
    if (item.shipment && !pageParam.bookingRequired) {
      const overviewItem = unifiedShipmentToOverviewItem(item.shipment)
      return overviewItem && withSearchFlags(overviewItem, item)
    }

    if (item.booking && !pageParam.shipmentRequired) {
      return withSearchFlags(unifiedBookingToOverviewItem(item.booking, resolvedAddresses, resolvedLegalEntities), item)
    }

    return null
  })
  return {
    items: overviewItems,
    totalItems,
    totalPages,
  }
}

export const unifiedOverviewInfiniteQueryOptions = (
  queryParams?: Partial<ShipmentPageParams>,
  shipmentRequired?: boolean,
  bookingRequired?: boolean
) => {
  const { query = '', ...restQueryParams } = queryParams || {}
  const initialPageParams: ShipmentPageParams = {
    limit: pageSize,
    offset: 0,
    orderBy: 'relevance',
    orderByDesc: true,
    query,
    shipmentRequired,
    bookingRequired,
    ...restQueryParams,
  }

  // Check and prevent empty search strings due to leading to empty result list
  if (query) {
    initialPageParams.query = query
  }

  return infiniteQueryOptions({
    queryKey: ['unifiedOverview', queryParams, shipmentRequired, bookingRequired],
    queryFn: getOverviewList,
    initialPageParam: initialPageParams,
    getNextPageParam: (
      result: PaginatedUnifiedShipments,
      _,
      pagination: ShipmentPageParams
    ): ShipmentPageParams | undefined => {
      const { totalItems } = result
      const { offset, limit } = pagination
      const isNextPagePresent = totalItems ? totalItems > offset + limit : undefined
      return isNextPagePresent ? { ...initialPageParams, offset: offset + limit } : undefined
    },
    getPreviousPageParam: () => undefined,
  })
}
